import { ParsedSlashCommand } from 'conversations/components/SlashCommandPopup/SlashCommandParser'
import { BadgeCounts } from 'conversations/hooks/useBadgeCounts'
import { byId as endUserById, getEndUser } from 'conversations/modules/endUser/selectors'
import {
  byId as userById,
  getCurrentUserId,
  getMarkAllAsReadPreference,
} from 'conversations/modules/user/selectors'
import { getDisplayName } from 'conversations/utils/nameUtils'
import { isRunningInDesktop } from 'conversations/utils/PlatformUtil'
import { convertToNumber, isDefined } from 'conversations/utils/typescriptHelpers'
import { createSelector } from 'reselect'
import { FETCH_ALL_CONVERSATIONS_REQUEST } from 'shared/store/actions/conversation/constants'
import { MESSAGE_READ_REQUEST } from 'shared/store/actions/message/constants'
import Conversation, { TranslationSettings } from 'shared/typings/conversations/Conversation'
import { Counts } from 'shared/typings/conversations/Counts'
import { FileUpload } from 'shared/typings/conversations/FileUpload'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { EndUser } from 'shared/typings/endUser/EndUser'
import { Message, MessageWithId } from 'shared/typings/messages/Message'
import { Tag } from 'shared/typings/tags/Tag'
import { UserIdType } from 'shared/typings/users/UserIdType'

import { RootState } from 'typesafe-actions'

import { getMessages } from '../message/selectors'

import { CloseAllConversationsStatus } from './reducer'

const newArray: any[] = []
const emptyObject: any = {}
type conversationUnreadStatusById = Record<number, string>

const root = (state: RootState) => state.convo.conversation

export const getAllConversations = (state: RootState) => state.convo.conversation.allConversations

export const getSubscriptionConversations = (subscriptionId: string) => (state: RootState) => {
  const allConvos = root(state).allConversations
  const convoIdsBySubscription = root(state).conversationsBySubscription

  return Object.values(allConvos).filter(({ id }) =>
    convoIdsBySubscription[subscriptionId]?.has(id)
  )
}

export const getHasMoreSidebarConversations =
  (viewType: string) =>
  (state: RootState): boolean | undefined =>
    root(state).allConversationsPagination[viewType]?.more

export const getConversationUnreadState = (state: RootState): conversationUnreadStatusById =>
  state.convo.conversation.conversationUnreadStatusById

export const getMostRecentReadMessageForConversation =
  (conversationId: number) =>
  (state: RootState): number =>
    root(state).mostRecentReadMessageIdByConversation[conversationId]

export const getPendingReadReceiptForConversation =
  (conversationId: number) =>
  (state: RootState): number =>
    root(state).pendingReadReceiptByConversation[conversationId]

export const getNextSidebarCursor = (state: RootState, viewType: string): string | undefined =>
  root(state).allConversationsPagination[viewType]?.next

export const fetchConversationListLoading =
  (viewType: string) =>
  (state: RootState): boolean =>
    root(state).fetchAllConversationsStatus[viewType] === FETCH_ALL_CONVERSATIONS_REQUEST

export const getConversation = (conversationId: number) => {
  return createSelector(getAllConversations, (conversations) => {
    return conversations[conversationId] || undefined
  })
}

export const getCurrentConversation = (state: RootState): Conversation | undefined => {
  const id = getConversationIdFromPathName()
  if (id === null || id === undefined) {
    return undefined
  }
  return getConversation(id)(state)
}

export const getCurrentConversationStatus = (state: RootState): ConversationStatus | undefined =>
  getCurrentConversation(state)?.status

export const getCurrentConversationTranslationSettings = (
  state: RootState
): TranslationSettings | undefined =>
  getCurrentConversation(state)?.conversationProperties?.translationSettings

export const getCurrentConversationExists = (state: RootState): boolean => {
  const conversationId = getConversationIdFromPathName()
  return (
    conversationId !== null &&
    conversationId !== undefined &&
    !!getConversation(conversationId)(state)
  )
}

export const getUserIsParticipantOfCurrentConversation =
  (userId: number | undefined) =>
  (state: RootState): boolean => {
    const participantIds = getCurrentConversation(state)?.participantIds ?? []
    return !!userId && participantIds.includes(userId)
  }

export const getTag =
  (tagId: number) =>
  (state: RootState): Tag | undefined =>
    root(state).tagsById[tagId]

export const getTagsById = (state: RootState): Record<number, Tag> => root(state).tagsById

export const getTags = createSelector(getTagsById, (tags) => Object.values(tags))

const getTypingUsers =
  (conversationId: number, currentUserId?: number) =>
  (state: RootState): string[] => {
    const userTypesByUserId = root(state).typingUserTypesByConvoId[conversationId]
    if (userTypesByUserId && currentUserId) {
      return Object.keys(userTypesByUserId)
        .filter((userId) => Number(userId) !== currentUserId)
        .map((u) => Number(u))
        .map((userId) => {
          if (userTypesByUserId[userId] === UserIdType.USER) {
            return getDisplayName(userById(state, userId))
          } else {
            return getDisplayName(endUserById(state)[userId])
          }
        })
    } else {
      return newArray
    }
  }

export const getTypingUsersDisplayNames =
  (conversationId?: number, currentUserId?: number) =>
  (state: RootState): string => {
    return (conversationId && getTypingUsers(conversationId, currentUserId)(state).join(' ')) || ''
  }

export const getNumTypingUsers =
  (conversationId?: number, currentUserId?: number) =>
  (state: RootState): number => {
    return (conversationId && getTypingUsers(conversationId, currentUserId)(state).length) || 0
  }

export const getEndUserByConversation =
  (conversation: Conversation | undefined) =>
  (state: RootState): EndUser | undefined =>
    conversation ? getEndUser(conversation.endUserId)(state) : undefined

export const getFileUploadsByConversationId =
  (conversationId?: number) =>
  (state: RootState): FileUpload[] => {
    return (conversationId && root(state).fileUploadsByConversationId[conversationId]) || newArray
  }

export const getHasFailedFileUpload =
  (uploadId: number) =>
  (state: RootState): boolean => {
    return !!root(state).failedFileUploadByUploadId[uploadId]
  }

export const getHasPendingAttachmentsByConversationId =
  (conversationId?: number) =>
  (state: RootState): boolean => {
    return (
      (conversationId &&
        getFileUploadsByConversationId(conversationId)(state)
          .filter((a) => !getHasFailedFileUpload(a.uploadId))
          .some((a) => !a.id)) ||
      false
    )
  }

export const getConversationIdFromPathName = (): number | undefined => {
  if (isRunningInDesktop && !window.Cypress) {
    return convertToNumber(window.location.hash.split('/')[2])
  } else {
    return convertToNumber(window.location.pathname.split('/')[2])
  }
}

const getIsNewMessage = (lastSent: number, lastRead: number): boolean => lastSent > lastRead

export const isConversationUnread =
  (convo: Conversation) =>
  (state: RootState): boolean => {
    const convoUnreadStatus = getConversationUnreadState(state)[convo.id]
    const markAllAsReadPrefs = getMarkAllAsReadPreference(state)
    if (convoUnreadStatus === MESSAGE_READ_REQUEST) {
      return false
    }

    const neverReadMessage = convo.lastReadMessageTimestamp === undefined
    if (neverReadMessage) {
      return true
    }

    const lastMessageReadTimestamp = Math.max(
      markAllAsReadPrefs[convo.inboxId] ?? 0,
      convo.lastReadMessageTimestamp ?? 0
    )
    return getIsNewMessage(convo.lastSentMessageTimestamp ?? 0, lastMessageReadTimestamp)
  }

export const getIsConversationUnread =
  (conversationId: number) =>
  (state: RootState): boolean => {
    const convo = getConversation(conversationId)(state)
    return convo ? isConversationUnread(convo)(state) : false
  }

const getSentMessagesForConversation = (
  state: RootState,
  conversationId: number
): MessageWithId[] => {
  const messages = getMessages(conversationId)(state)

  if (!messages) {
    return []
  }
  return messages.filter((msg: Message) => msg.id !== undefined && msg.id > -1) as MessageWithId[]
}

export const getUnreadMessagesForConversation = (
  state: RootState,
  conversationId: number
): MessageWithId[] => {
  const conversation = getConversation(conversationId)(state)

  const messagesForConversation = getSentMessagesForConversation(state, conversationId)
  if (messagesForConversation && conversation) {
    return messagesForConversation.filter((msg) =>
      conversation.lastReadMessageTimestamp
        ? msg.createdAt > conversation.lastReadMessageTimestamp
        : true
    )
  } else {
    return []
  }
}

// Don't use this selector in a component without memoing it
export const getMyOpenConversationIds = (state: RootState): number[] => {
  const myUserId = getCurrentUserId(state)
  return Object.values(getAllConversations(state))
    .filter(isDefined)
    .filter((convo) => convo.status === ConversationStatus.OPEN)
    .filter((convo) => !!myUserId && convo.participantIds.includes(myUserId))
    .map((convo) => convo.id)
}

// Don't use this selector in a component without memoing it
export const getNumberOfUnreadConversations = (state: RootState): number => {
  const allConvos = getAllConversations(state)
  const userId = getCurrentUserId(state)

  return Object.keys(allConvos).reduce((acc: number, conversationId: string): number => {
    const conversation = allConvos[Number(conversationId)]
    if (
      getIsConversationUnread(Number(conversationId))(state) &&
      getMyOpenConversationIds(state).includes(Number(conversationId)) &&
      userId &&
      conversation?.participantIds?.includes(userId)
    ) {
      acc += 1
    }
    return acc
  }, 0)
}

export const getLastSyncTime = (state: RootState): number | undefined =>
  root(state).syncTime ?? undefined

export const getRoutedUnrespondedConversations = (state: RootState): number[] =>
  root(state).routedNotRespondedConversations ?? newArray

export const getCounts =
  (sectionUUID: string) =>
  (state: RootState): Counts => {
    return root(state).countsBySectionUUID[sectionUUID] ?? emptyObject
  }

export const getBadgeCounts =
  () =>
  (state: RootState): BadgeCounts => {
    return root(state).badgeCounts ?? emptyObject
  }

export const getSlashCommand = (state: RootState): ParsedSlashCommand | null => {
  return root(state).slashCommand
}

export const isConversationTranslationSettingsVisible = (state: RootState): boolean =>
  root(state).translationSettingsVisibility

export const getCloseAllConversationsStatus = (state: RootState): CloseAllConversationsStatus =>
  root(state).closeAllConversationsStatus
